import { useCallback, useEffect, useMemo, useState } from "react";
import { ChefHat, Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  downloadProductionReportExport,
  fetchProductionReport,
  type ProductionReport,
  type ProductionReportLine,
} from "@/lib/adminProductionReportApi";
import { fetchAdminCustomers } from "@/lib/adminCustomersApi";
import type { AdminClientSummary } from "@/lib/types";
import { hasPermission } from "@/lib/types";
import { getApiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useApp } from "@/store/useApp";

type ProductFilter = "ALL" | "NATURA" | "PROCESSADO";

const FILTER_TABS: { id: ProductFilter; label: string }[] = [
  { id: "ALL", label: "Todos" },
  { id: "NATURA", label: "In natura" },
  { id: "PROCESSADO", label: "Processados" },
];

function matchesFilter(line: ProductionReportLine, filter: ProductFilter): boolean {
  if (filter === "NATURA") return !line.isProcessed;
  if (filter === "PROCESSADO") return line.isProcessed;
  return true;
}

function lineKey(prefix: string, line: ProductionReportLine): string {
  return `${prefix}-${line.productId}-${line.unit}-${line.cut ?? ""}-${line.isProcessed ? "p" : "n"}`;
}

function CutBadge({ line }: { line: ProductionReportLine }) {
  const label = line.isProcessed ? line.cut ?? "Processado" : line.cut ?? "In natura";
  return (
    <span
      className={cn(
        "ml-2 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        line.isProcessed
          ? "bg-primary/10 text-primary"
          : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
      )}
    >
      {label}
    </span>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function customerLabel(c: AdminClientSummary): string {
  return c.tradeName || c.companyName || c.email;
}

function fmtQty(value: number): string {
  if (!value) return "—";
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function AdminProductionReport() {
  // SEPARACAO só recebe itens in natura do backend (o filtro é aplicado lá) — aqui só travamos
  // a aba na visão correspondente para não mostrar abas que sempre voltam vazias.
  const isSeparacao = useApp((s) => s.account?.role) === "SEPARACAO";
  const canExport = hasPermission(useApp((s) => s.account), "RELATORIOS.PRODUCAO.EXPORTAR");
  const canViewClients = hasPermission(useApp((s) => s.account), "CLIENTES.CLIENTE.VISUALIZAR");
  const [date, setDate] = useState(todayIso());
  const [customerId, setCustomerId] = useState<string>("");
  const [groupByCustomer, setGroupByCustomer] = useState(true);
  const [customers, setCustomers] = useState<AdminClientSummary[]>([]);
  const [report, setReport] = useState<ProductionReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "xlsx" | null>(null);
  const [productFilter, setProductFilter] = useState<ProductFilter>(isSeparacao ? "NATURA" : "ALL");

  const filteredLines = useMemo(
    () => (report?.lines ?? []).filter((line) => matchesFilter(line, productFilter)),
    [report, productFilter],
  );
  const inNaturaLines = useMemo(
    () => filteredLines.filter((line) => !line.isProcessed),
    [filteredLines],
  );
  const processadosLines = useMemo(
    () => filteredLines.filter((line) => line.isProcessed),
    [filteredLines],
  );

  useEffect(() => {
    // Filtro de cliente é auxiliar: sem CLIENTES.CLIENTE.VISUALIZAR o relatório segue funcionando sem ele.
    if (!hasPermission(useApp.getState().account, "CLIENTES.CLIENTE.VISUALIZAR")) return;
    fetchAdminCustomers()
      .then((list) => setCustomers(list))
      .catch(() => {});
  }, []);

  const sortedCustomers = useMemo(
    () => [...customers].sort((a, b) => customerLabel(a).localeCompare(customerLabel(b))),
    [customers],
  );

  const handleGenerate = useCallback(async () => {
    if (!date) {
      toast.error("Informe a data");
      return;
    }
    setLoading(true);
    try {
      const result = await fetchProductionReport({
        from: date,
        to: date,
        customerId: customerId || null,
        groupByCustomer,
      });
      setReport(result);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Erro ao gerar relatório"));
    } finally {
      setLoading(false);
    }
  }, [date, customerId, groupByCustomer]);

  const handleExport = useCallback(
    async (format: "pdf" | "xlsx") => {
      if (!date) {
        toast.error("Informe a data");
        return;
      }
      setExporting(format);
      try {
        const { filename, blob } = await downloadProductionReportExport(
          { from: date, to: date, customerId: customerId || null, groupByCustomer },
          format,
        );
        downloadBlob(filename, blob);
      } catch (e) {
        toast.error(getApiErrorMessage(e, "Erro ao exportar"));
      } finally {
        setExporting(null);
      }
    },
    [date, customerId, groupByCustomer],
  );

  const totals = report?.totals;

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ChefHat className="h-5 w-5" /> Relatório de Produção
            </h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Quantidades solicitadas e produzidas por produto e cliente.
            </p>
          </div>
          {report && canExport && (
            <div className="flex gap-2">
              <Button variant="secondary" className="gap-2" onClick={() => void handleExport("pdf")} disabled={exporting !== null}>
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                PDF
              </Button>
              <Button variant="secondary" className="gap-2" onClick={() => void handleExport("xlsx")} disabled={exporting !== null}>
                {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                XLSX
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[1fr_2fr_auto] items-end">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Data</Label>
            <DatePicker value={date} onChange={setDate} />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Cliente</Label>
            <Select value={customerId || "__all__"} disabled={!canViewClients} onValueChange={(v) => setCustomerId(v === "__all__" ? "" : v)}>
              <SelectTrigger className="disabled:cursor-not-allowed disabled:opacity-60"
                title={canViewClients ? undefined : "Filtro indisponível: requer acesso a Clientes"}>
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os clientes</SelectItem>
                {sortedCustomers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{customerLabel(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={() => void handleGenerate()} disabled={loading} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChefHat className="h-4 w-4" />}
            Gerar
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t pt-3">
          <div className="inline-flex items-center gap-1 rounded-lg border bg-secondary/30 p-1">
            {(isSeparacao ? FILTER_TABS.filter((tab) => tab.id === "NATURA") : FILTER_TABS).map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setProductFilter(tab.id)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                  productFilter === tab.id
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={groupByCustomer} onCheckedChange={setGroupByCustomer} />
            <Label className="text-xs text-muted-foreground">Detalhar por cliente</Label>
          </div>
        </div>
      </div>

      {report && totals && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Pedidos" value={String(report.orderCount)} />
          <StatCard label="Produtos" value={String(totals.productCount)} />
          <StatCard label="Solicitado" value={fmtQty(totals.requestedQty)} />
          <StatCard label="Produzido" value={fmtQty(totals.producedQty)} />
        </div>
      )}

      {report && filteredLines.length === 0 && (
        <div className="rounded-xl border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          Nenhum produto encontrado nesse filtro.
        </div>
      )}

      {/* O consolidado só aparece quando NÃO está detalhando por cliente — senão a informação
          fica duplicada (cada cliente já lista seus itens). */}
      {report && !groupByCustomer && inNaturaLines.length > 0 && (productFilter === "ALL" || productFilter === "NATURA") && (
        <ConsolidatedSection title="Produtos In Natura" lines={inNaturaLines} accent="emerald" />
      )}

      {report && !groupByCustomer && processadosLines.length > 0 && (productFilter === "ALL" || productFilter === "PROCESSADO") && (
        <ConsolidatedSection title="Produtos Processados" lines={processadosLines} accent="primary" />
      )}

      {report && report.byCustomer && report.byCustomer.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-display text-lg font-semibold">Detalhamento por cliente</h2>
          {report.byCustomer.map((c, idx) => {
            const customerLines = c.lines.filter((line) => matchesFilter(line, productFilter));
            if (customerLines.length === 0) return null;
            // Chave única por pedido — dois pedidos do mesmo cliente/unidade têm o mesmo nome, então
            // o orderNumber (com o índice como reforço) evita colisão de key no React.
            const blockKey = `bd-${idx}-${c.orderNumber ?? c.customerId ?? "none"}`;
            return (
              <div key={blockKey} className="rounded-xl border bg-card shadow-sm">
                <div className="border-b px-4 py-3">
                  <div className="flex items-center gap-2 font-medium">
                    {c.orderNumber != null && (
                      <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-xs font-semibold text-primary">
                        Pedido #{c.orderNumber}
                      </span>
                    )}
                    <span>{c.customerName ?? c.customerId}</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                    {c.productionDate && <span>Produção: <strong className="font-medium text-foreground">{c.productionDate}</strong></span>}
                    {c.deliveryDate && <span>Entrega: <strong className="font-medium text-foreground">{c.deliveryDate}</strong></span>}
                    {c.deliveryObs && <span>Entrega ao cliente: <strong className="font-medium text-foreground">{c.deliveryObs}</strong></span>}
                    <span>{c.orderCount} pedido(s) · {customerLines.length} item(ns)</span>
                  </div>
                  {c.notes && (
                    <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                      <span className="font-semibold">Observação do cliente: </span>
                      <span className="whitespace-pre-wrap break-words">{c.notes}</span>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="px-4 py-2 text-left">Produto</th>
                        <th className="px-4 py-2 text-center">Unidade</th>
                        <th className="px-4 py-2 text-right">Solicitado</th>
                        <th className="px-4 py-2 text-right">Produzido</th>
                        <th className="px-4 py-2 text-left">Observação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerLines.map((l) => (
                        <tr key={lineKey(blockKey, l)} className="border-t">
                          <td className="px-4 py-2">
                            <span className="font-medium">{l.productName}</span>
                            <CutBadge line={l} />
                          </td>
                          <td className="px-4 py-2 text-center text-muted-foreground">{l.unit}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{fmtQty(l.requestedQty)}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-green-700">{fmtQty(l.producedQty)}</td>
                          <td className="px-4 py-2 text-muted-foreground whitespace-pre-wrap break-words">{l.observation ?? ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!report && !loading && (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/30 py-16 text-center text-sm text-muted-foreground">
          <Download className="h-6 w-6" />
          Informe o período e clique em <strong>Gerar</strong> para ver o relatório.
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function ConsolidatedSection({
  title,
  lines,
  accent,
}: {
  title: string;
  lines: ProductionReportLine[];
  accent: "primary" | "emerald";
}) {
  const accentClass = accent === "primary"
    ? "bg-primary/10 text-primary"
    : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300";
  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="text-sm font-medium">{title}</div>
        <span className={cn("rounded-md px-2 py-0.5 text-xs font-medium", accentClass)}>
          {lines.length} {lines.length === 1 ? "item" : "itens"}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2 text-left">Produto</th>
              <th className="px-4 py-2 text-center">Unidade</th>
              <th className="px-4 py-2 text-right">Solicitado</th>
              <th className="px-4 py-2 text-right">Produzido</th>
              <th className="px-4 py-2 text-center">Status</th>
              <th className="px-4 py-2 text-left">Observação</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={lineKey("consol", l)} className="border-t">
                <td className="px-4 py-2">
                  <span className="font-medium">{l.productName}</span>
                  <CutBadge line={l} />
                </td>
                <td className="px-4 py-2 text-center text-muted-foreground">{l.unit}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtQty(l.requestedQty)}</td>
                <td className="px-4 py-2 text-right tabular-nums text-green-700">{fmtQty(l.producedQty)}</td>
                <td className="px-4 py-2 text-center text-xs">
                  {Object.entries(l.statusCounts).map(([s, n]) => (
                    <span key={s} className="mr-2 inline-block whitespace-nowrap">
                      {s}: {n}
                    </span>
                  ))}
                </td>
                <td className="px-4 py-2 text-muted-foreground whitespace-pre-wrap break-words">{l.observation ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
